import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Crown, Clock, CreditCard, TrendingUp, Shield, RefreshCw, UserPlus, UserMinus, FolderOpen, Save, Settings, Gamepad2, Server, DollarSign, Calendar, Flag, Plus, Minus, Trash2, Timer } from 'lucide-react';
import AdminSpeedhiveCategories from './AdminSpeedhiveCategories';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../config/api';

const AdminPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState('users');
  
  // Drive config state
  const [driveConfig, setDriveConfig] = useState({
    turismo_pista_folder_id: '',
    carshow_folder_id: '',
    turismo_nacional_folder_id: ''
  });
  const [savingDriveConfig, setSavingDriveConfig] = useState(false);
  
  // Simracing server config state
  const [simracingConfig, setSimracingConfig] = useState({
    server_name: '',
    track_name: '',
    server_link: '',
    live_timing_url: '',
    max_players: '24'
  });
  const [savingSimracingConfig, setSavingSimracingConfig] = useState(false);
  
  // Pricing config state
  const [pricingConfig, setPricingConfig] = useState({
    pro_monthly: 37500,
    pro_annual: 125000,
    simracing_monthly: 30000
  });
  const [savingPricing, setSavingPricing] = useState(false);
  
  // Championship rounds config state
  const [championshipRounds, setChampionshipRounds] = useState({
    turismo_pista: Array(12).fill(null).map((_, i) => ({ round: i + 1, name: '', drive_link: '' })),
    carshow: Array(12).fill(null).map((_, i) => ({ round: i + 1, name: '', drive_link: '' })),
    turismo_nacional: Array(14).fill(null).map((_, i) => ({ round: i + 1, name: '', drive_link: '' }))
  });
  const [savingChampionship, setSavingChampionship] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, paymentsRes, driveRes, simracingRes, pricingRes, championshipRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/payments`, { withCredentials: true }),
        axios.get(`${API}/drive/folder-config`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/admin/simracing-config`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API}/payments/prices`, { withCredentials: true }).catch(() => ({ data: { prices: {} } })),
        axios.get(`${API}/admin/championship-rounds`, { withCredentials: true }).catch(() => ({ data: {} }))
      ]);
      
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPayments(paymentsRes.data.transactions);
      setDriveConfig({
        turismo_pista_folder_id: driveRes.data.turismo_pista_folder_id || '',
        carshow_folder_id: driveRes.data.carshow_folder_id || '',
        turismo_nacional_folder_id: driveRes.data.turismo_nacional_folder_id || ''
      });
      setSimracingConfig({
        server_name: simracingRes.data.server_name || '',
        track_name: simracingRes.data.track_name || '',
        server_link: simracingRes.data.server_link || '',
        live_timing_url: simracingRes.data.live_timing_url || '',
        max_players: simracingRes.data.max_players || '24'
      });
      setPricingConfig({
        pro_monthly: pricingRes.data.prices?.pro_monthly || 37500,
        pro_annual: pricingRes.data.prices?.pro_annual || 125000,
        simracing_monthly: pricingRes.data.prices?.simracing_monthly || 30000
      });
      setChampionshipRounds({
        turismo_pista: championshipRes.data?.turismo_pista || Array(12).fill(null).map((_, i) => ({ round: i + 1, name: '', drive_link: '' })),
        carshow: championshipRes.data?.carshow || Array(12).fill(null).map((_, i) => ({ round: i + 1, name: '', drive_link: '' })),
        turismo_nacional: championshipRes.data?.turismo_nacional || Array(14).fill(null).map((_, i) => ({ round: i + 1, name: '', drive_link: '' }))
      });
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('No tienes permisos de administrador');
        navigate('/app');
      } else if (error.response?.status === 401) {
        toast.error('Debes iniciar sesión');
        navigate('/login');
      } else {
        toast.error('Error al cargar datos');
      }
    } finally {
      setLoading(false);
    }
  };

  const activatePro = async (userId, days) => {
    // If called with days=-1 it means "custom" — prompt the admin
    if (days === 'custom') {
      const v = window.prompt('¿Por cuántos días activás PRO?', '30');
      if (v == null) return;
      const n = parseInt(v, 10);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error('Cantidad de días inválida');
        return;
      }
      days = n;
    }
    try {
      await axios.post(`${API}/admin/users/${userId}/activate-pro?days=${days}`, {}, { withCredentials: true });
      toast.success(`PRO activado por ${days} días`);
      loadData();
    } catch (error) {
      toast.error('Error al activar PRO');
    }
  };

  const deactivatePro = async (userId) => {
    if (!window.confirm('¿Desactivar PRO para este usuario?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/deactivate-pro`, {}, { withCredentials: true });
      toast.success('PRO desactivado');
      loadData();
    } catch (error) {
      toast.error('Error al desactivar PRO');
    }
  };

  const activateSimracing = async (userId, days) => {
    if (days === 'custom') {
      const v = window.prompt('¿Por cuántos días activás Simracing?', '30');
      if (v == null) return;
      const n = parseInt(v, 10);
      if (!Number.isFinite(n) || n <= 0) {
        toast.error('Cantidad de días inválida');
        return;
      }
      days = n;
    }
    try {
      await axios.post(`${API}/admin/users/${userId}/activate-simracing?days=${days}`, {}, { withCredentials: true });
      toast.success(`Simracing activado por ${days} días`);
      loadData();
    } catch (error) {
      toast.error('Error al activar Simracing');
    }
  };

  const deactivateSimracing = async (userId) => {
    if (!window.confirm('¿Desactivar Simracing para este usuario?')) return;
    try {
      await axios.post(`${API}/admin/users/${userId}/deactivate-simracing`, {}, { withCredentials: true });
      toast.success('Simracing desactivado');
      loadData();
    } catch (error) {
      toast.error('Error al desactivar Simracing');
    }
  };

  // Add or subtract days from the current subscription without changing plan.
  // Use positive numbers to extend, negative to shorten.
  const adjustDays = async (userId, userEmail, direction) => {
    const verb = direction === 'add' ? 'SUMAR' : 'RESTAR';
    const v = window.prompt(
      `¿Cuántos días querés ${verb} a la suscripción de ${userEmail}?`,
      '7',
    );
    if (v == null) return;
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error('Cantidad de días inválida');
      return;
    }
    const signedDays = direction === 'add' ? n : -n;
    try {
      const resp = await axios.post(
        `${API}/admin/users/${userId}/adjust-days?days=${signedDays}`,
        {}, { withCredentials: true },
      );
      const msg = resp?.data?.downgraded
        ? `${verb === 'SUMAR' ? 'Se sumaron' : 'Se restaron'} ${n} días · usuario degradado a free`
        : `${verb === 'SUMAR' ? 'Se sumaron' : 'Se restaron'} ${n} días a la suscripción`;
      toast.success(msg);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al ajustar días');
    }
  };

  // Permanently delete a user account (admin-only). Asks for explicit
  // confirmation including typing the email to prevent accidents.
  const deleteUser = async (userId, userEmail) => {
    const prompt1 = window.confirm(
      `⚠️ Vas a eliminar PERMANENTEMENTE la cuenta de ${userEmail}.\n\n` +
      'Se borran: sesiones, favoritos y timers del usuario.\n' +
      'Las carreras analizadas y el histórico de pagos NO se borran.\n\n' +
      '¿Seguro que querés continuar?',
    );
    if (!prompt1) return;
    const typed = window.prompt(`Para confirmar, escribí el email exacto:\n\n${userEmail}`);
    if (typed == null) return;
    if (typed.trim().toLowerCase() !== userEmail.toLowerCase()) {
      toast.error('El email no coincide — cancelado por seguridad');
      return;
    }
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success(`Cuenta ${userEmail} eliminada`);
      loadData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Error al eliminar cuenta');
    }
  };

  // Toggle is_admin or pit_access on a user. Optimistic update with toast.
  const togglePermission = async (userId, key, nextValue, userEmail) => {
    if (key === 'is_admin' && !nextValue) {
      const ok = window.confirm(`¿Quitar permisos de admin a ${userEmail}? El usuario perderá acceso al panel.`);
      if (!ok) return;
    }
    const prevUsers = users;
    setUsers((u) => u.map((x) => x.user_id === userId ? { ...x, [key]: nextValue } : x));
    try {
      await axios.patch(
        `${API}/admin/users/${userId}/permissions`,
        { [key]: nextValue },
        { withCredentials: true },
      );
      toast.success(
        key === 'is_admin'
          ? (nextValue ? 'Permisos de admin otorgados' : 'Permisos de admin revocados')
          : (nextValue ? 'Acceso a Ingeniería otorgado' : 'Acceso a Ingeniería revocado'),
      );
    } catch (error) {
      setUsers(prevUsers);
      toast.error(error?.response?.data?.detail || 'No se pudo actualizar el permiso');
    }
  };

  const saveDriveConfig = async () => {
    setSavingDriveConfig(true);
    try {
      const response = await axios.post(`${API}/drive/folder-config`, driveConfig, { withCredentials: true });
      toast.success('Configuración de Drive guardada');
      setDriveConfig({
        turismo_pista_folder_id: response.data.turismo_pista_folder_id || '',
        carshow_folder_id: response.data.carshow_folder_id || '',
        turismo_nacional_folder_id: response.data.turismo_nacional_folder_id || ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSavingDriveConfig(false);
    }
  };

  const saveSimracingConfig = async () => {
    setSavingSimracingConfig(true);
    try {
      await axios.post(`${API}/admin/simracing-config`, simracingConfig, { withCredentials: true });
      toast.success('Configuración de Simracing guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar configuración');
    } finally {
      setSavingSimracingConfig(false);
    }
  };

  const savePricingConfig = async () => {
    setSavingPricing(true);
    try {
      await axios.post(`${API}/payments/prices`, pricingConfig, { withCredentials: true });
      toast.success('Precios actualizados correctamente');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar precios');
    } finally {
      setSavingPricing(false);
    }
  };

  const saveChampionshipRounds = async () => {
    setSavingChampionship(true);
    try {
      await axios.post(`${API}/admin/championship-rounds`, championshipRounds, { withCredentials: true });
      toast.success('Fechas del campeonato guardadas');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar fechas');
    } finally {
      setSavingChampionship(false);
    }
  };

  const updateChampionshipRound = (category, roundIndex, field, value) => {
    setChampionshipRounds(prev => ({
      ...prev,
      [category]: prev[category].map((r, i) => 
        i === roundIndex ? { ...r, [field]: value } : r
      )
    }));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-AR', { 
      style: 'currency', 
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-neutral-900 border-b border-red-900/30">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/app">
                <Button variant="ghost" className="text-gray-400 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Volver
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Shield className="w-6 h-6 text-red-500" />
                <h1 className="text-xl font-bold">Panel de Administración</h1>
              </div>
            </div>
            <Button onClick={loadData} variant="outline" className="border-red-600 text-red-500">
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualizar
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="bg-neutral-900 border-neutral-700">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats.total_users}</p>
                    <p className="text-sm text-gray-400">Usuarios Total</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-neutral-900 border-neutral-700">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Crown className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats.pro_users}</p>
                    <p className="text-sm text-gray-400">Usuarios PRO</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-neutral-900 border-neutral-700">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Gamepad2 className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats.simracing_users || 0}</p>
                    <p className="text-sm text-gray-400">Simracing</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-neutral-900 border-neutral-700">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats.approved_payments}</p>
                    <p className="text-sm text-gray-400">Pagos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-neutral-900 border-neutral-700">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <TrendingUp className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-2xl font-bold">{stats.conversion_rate}%</p>
                    <p className="text-sm text-gray-400">Conversión</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          <Button
            onClick={() => setActiveTab('users')}
            className={activeTab === 'users' ? 'bg-red-600' : 'bg-neutral-800'}
          >
            <Users className="w-4 h-4 mr-2" />
            Usuarios ({users.length})
          </Button>
          <Button
            onClick={() => setActiveTab('payments')}
            className={activeTab === 'payments' ? 'bg-red-600' : 'bg-neutral-800'}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            Pagos ({payments.length})
          </Button>
          <Button
            onClick={() => setActiveTab('config')}
            className={activeTab === 'config' ? 'bg-red-600' : 'bg-neutral-800'}
          >
            <Settings className="w-4 h-4 mr-2" />
            Configuración
          </Button>
          <Button
            onClick={() => setActiveTab('simracing')}
            className={activeTab === 'simracing' ? 'bg-orange-600' : 'bg-neutral-800'}
          >
            <Gamepad2 className="w-4 h-4 mr-2" />
            Simracing
          </Button>
          <Button
            onClick={() => setActiveTab('pricing')}
            className={activeTab === 'pricing' ? 'bg-green-600' : 'bg-neutral-800'}
          >
            <DollarSign className="w-4 h-4 mr-2" />
            Precios
          </Button>
          <Button
            onClick={() => setActiveTab('championship')}
            className={activeTab === 'championship' ? 'bg-blue-600' : 'bg-neutral-800'}
          >
            <Calendar className="w-4 h-4 mr-2" />
            Fechas
          </Button>
          <Button
            onClick={() => setActiveTab('speedhive')}
            className={activeTab === 'speedhive' ? 'bg-red-600' : 'bg-neutral-800'}
            data-testid="admin-tab-speedhive"
          >
            <Timer className="w-4 h-4 mr-2" />
            Cronometraje
          </Button>
        </div>

        {/* Speedhive Categories Tab */}
        {activeTab === 'speedhive' && <AdminSpeedhiveCategories />}

        {/* Championship Rounds Config Tab */}
        {activeTab === 'championship' && (
          <Card className="bg-neutral-900 border-neutral-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Flag className="w-5 h-5 text-blue-500" />
                Fechas del Campeonato
              </CardTitle>
              <CardDescription>
                Configura las fechas de cada categoría (Turismo Pista y CarShow: 12, Turismo Nacional: 14). Solo aparecerán las fechas que tengan nombre y link completos.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-8">
              {/* Turismo Pista */}
              <div>
                <h3 className="text-lg font-semibold text-red-500 mb-4 flex items-center gap-2">
                  <Badge className="bg-red-600">Turismo Pista</Badge>
                </h3>
                <div className="grid gap-3">
                  {championshipRounds.turismo_pista.map((round, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <span className="col-span-1 text-gray-500 text-sm font-mono">F{round.round}</span>
                      <Input
                        placeholder={`Nombre fecha ${round.round} (ej: San Jorge)`}
                        value={round.name}
                        onChange={(e) => updateChampionshipRound('turismo_pista', idx, 'name', e.target.value)}
                        className="col-span-4 bg-neutral-800 border-neutral-600 text-white text-sm"
                      />
                      <Input
                        placeholder="Link de Google Drive..."
                        value={round.drive_link}
                        onChange={(e) => updateChampionshipRound('turismo_pista', idx, 'drive_link', e.target.value)}
                        className="col-span-7 bg-neutral-800 border-neutral-600 text-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* CarShow */}
              <div>
                <h3 className="text-lg font-semibold text-orange-500 mb-4 flex items-center gap-2">
                  <Badge className="bg-orange-600">CarShow Santafesino</Badge>
                </h3>
                <div className="grid gap-3">
                  {championshipRounds.carshow.map((round, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <span className="col-span-1 text-gray-500 text-sm font-mono">F{round.round}</span>
                      <Input
                        placeholder={`Nombre fecha ${round.round}`}
                        value={round.name}
                        onChange={(e) => updateChampionshipRound('carshow', idx, 'name', e.target.value)}
                        className="col-span-4 bg-neutral-800 border-neutral-600 text-white text-sm"
                      />
                      <Input
                        placeholder="Link de Google Drive..."
                        value={round.drive_link}
                        onChange={(e) => updateChampionshipRound('carshow', idx, 'drive_link', e.target.value)}
                        className="col-span-7 bg-neutral-800 border-neutral-600 text-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Turismo Nacional */}
              <div>
                <h3 className="text-lg font-semibold text-blue-500 mb-4 flex items-center gap-2">
                  <Badge className="bg-blue-600">Turismo Nacional</Badge>
                  <span className="text-xs text-gray-500 font-normal">14 fechas</span>
                </h3>
                <div className="grid gap-3">
                  {championshipRounds.turismo_nacional.map((round, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <span className="col-span-1 text-gray-500 text-sm font-mono">F{round.round}</span>
                      <Input
                        data-testid={`tn-round-${round.round}-name`}
                        placeholder={`Nombre fecha ${round.round}`}
                        value={round.name}
                        onChange={(e) => updateChampionshipRound('turismo_nacional', idx, 'name', e.target.value)}
                        className="col-span-4 bg-neutral-800 border-neutral-600 text-white text-sm"
                      />
                      <Input
                        data-testid={`tn-round-${round.round}-link`}
                        placeholder="Link de Google Drive..."
                        value={round.drive_link}
                        onChange={(e) => updateChampionshipRound('turismo_nacional', idx, 'drive_link', e.target.value)}
                        className="col-span-7 bg-neutral-800 border-neutral-600 text-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              </div>

              <div className="pt-4 border-t border-neutral-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-400">
                    <p>Solo las fechas con nombre y link aparecerán en la sección de análisis.</p>
                  </div>
                  <Button 
                    onClick={saveChampionshipRounds} 
                    disabled={savingChampionship}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {savingChampionship ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Fechas
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Pricing Config Tab */}
        {activeTab === 'pricing' && (
          <Card className="bg-neutral-900 border-neutral-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-500" />
                Configuración de Precios
              </CardTitle>
              <CardDescription>
                Modifica los precios de los planes de suscripción (en pesos argentinos)
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-yellow-500" />
                    PRO Mensual (ARS)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="100"
                    value={pricingConfig.pro_monthly}
                    onChange={(e) => setPricingConfig({...pricingConfig, pro_monthly: parseFloat(e.target.value) || 0})}
                    className="bg-neutral-800 border-neutral-600 text-white"
                  />
                  <p className="text-xs text-gray-500">Actual: {formatPrice(pricingConfig.pro_monthly)}</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Crown className="w-4 h-4 text-yellow-500" />
                    PRO Anual (ARS)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="1000"
                    value={pricingConfig.pro_annual}
                    onChange={(e) => setPricingConfig({...pricingConfig, pro_annual: parseFloat(e.target.value) || 0})}
                    className="bg-neutral-800 border-neutral-600 text-white"
                  />
                  <p className="text-xs text-gray-500">Actual: {formatPrice(pricingConfig.pro_annual)}</p>
                </div>
                
                <div className="space-y-2">
                  <Label className="text-gray-300 flex items-center gap-2">
                    <Gamepad2 className="w-4 h-4 text-orange-500" />
                    Simracing Mensual (ARS)
                  </Label>
                  <Input
                    type="number"
                    min="0"
                    step="1000"
                    value={pricingConfig.simracing_monthly}
                    onChange={(e) => setPricingConfig({...pricingConfig, simracing_monthly: parseFloat(e.target.value) || 0})}
                    className="bg-neutral-800 border-neutral-600 text-white"
                  />
                  <p className="text-xs text-gray-500">Actual: {formatPrice(pricingConfig.simracing_monthly)}</p>
                </div>
              </div>
              
              <div className="pt-4 border-t border-neutral-700">
                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-400">
                    <p>Los cambios se aplicarán a nuevas suscripciones.</p>
                    <p>Las suscripciones existentes no se ven afectadas.</p>
                  </div>
                  <Button 
                    onClick={savePricingConfig} 
                    disabled={savingPricing}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    {savingPricing ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Guardando...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Guardar Precios
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Simracing Config Tab */}
        {activeTab === 'simracing' && (
          <Card className="bg-neutral-900 border-neutral-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Server className="w-5 h-5 text-orange-500" />
                Configuración del Servidor Assetto Corsa
              </CardTitle>
              <CardDescription>
                Configura los datos del servidor de Simracing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="server_name" className="text-gray-300">Nombre del Servidor</Label>
                  <Input
                    id="server_name"
                    placeholder="Ej: DATAPRO Racing Server"
                    value={simracingConfig.server_name}
                    onChange={(e) => setSimracingConfig({...simracingConfig, server_name: e.target.value})}
                    className="bg-neutral-800 border-neutral-600 text-white"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="track_name" className="text-gray-300">Circuito Actual</Label>
                  <Input
                    id="track_name"
                    placeholder="Ej: Autódromo de Buenos Aires"
                    value={simracingConfig.track_name}
                    onChange={(e) => setSimracingConfig({...simracingConfig, track_name: e.target.value})}
                    className="bg-neutral-800 border-neutral-600 text-white"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="server_link" className="text-gray-300">Link de Conexión al Servidor</Label>
                <Input
                  id="server_link"
                  placeholder="Ej: https://acstuff.club/s/server123 o acmanager://join?ip=..."
                  value={simracingConfig.server_link}
                  onChange={(e) => setSimracingConfig({...simracingConfig, server_link: e.target.value})}
                  className="bg-neutral-800 border-neutral-600 text-white"
                />
                <p className="text-xs text-gray-500">
                  Este es el link que verán los usuarios para conectarse al servidor
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="live_timing_url" className="text-gray-300">URL del Live Timing</Label>
                <Input
                  id="live_timing_url"
                  placeholder="Ej: http://45.235.98.204:27215/live-timing"
                  value={simracingConfig.live_timing_url}
                  onChange={(e) => setSimracingConfig({...simracingConfig, live_timing_url: e.target.value})}
                  className="bg-neutral-800 border-neutral-600 text-white"
                />
                <p className="text-xs text-gray-500">
                  URL de la página de tiempos en vivo (se mostrará embebida)
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="max_players" className="text-gray-300">Máx. Jugadores</Label>
                <Input
                  id="max_players"
                  placeholder="24"
                  value={simracingConfig.max_players}
                  onChange={(e) => setSimracingConfig({...simracingConfig, max_players: e.target.value})}
                  className="bg-neutral-800 border-neutral-600 text-white w-32"
                />
              </div>
              
              <div className="pt-4 border-t border-neutral-700">
                <Button 
                  onClick={saveSimracingConfig}
                  disabled={savingSimracingConfig}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {savingSimracingConfig ? 'Guardando...' : 'Guardar Configuración Simracing'}
                </Button>
              </div>
              
              {/* Preview */}
              {simracingConfig.server_link && (
                <div className="mt-6 p-4 bg-black/50 rounded-lg border border-orange-900/30">
                  <h4 className="text-sm font-semibold text-orange-500 mb-2">Vista Previa - Lo que verán los usuarios</h4>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-gray-400">Servidor: </span>
                      <span className="text-white">{simracingConfig.server_name || 'Sin nombre'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Circuito: </span>
                      <span className="text-white">{simracingConfig.track_name || 'No especificado'}</span>
                    </div>
                    <div>
                      <span className="text-gray-400">Link: </span>
                      <a href={simracingConfig.server_link} target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">
                        {simracingConfig.server_link}
                      </a>
                    </div>
                    <div>
                      <span className="text-gray-400">Capacidad: </span>
                      <span className="text-white">{simracingConfig.max_players} jugadores</span>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Configuration Tab */}
        {activeTab === 'config' && (
          <Card className="bg-neutral-900 border-neutral-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <FolderOpen className="w-5 h-5 text-blue-500" />
                Configuración de Carpetas en la Nube
              </CardTitle>
              <CardDescription className="text-gray-400">
                Pega el link de la carpeta para cada categoría. 
                Soporta Google Drive, IceDrive y otros servicios de nube pública.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Turismo Pista */}
              <div className="space-y-2">
                <Label htmlFor="turismo_drive" className="text-white flex items-center gap-2">
                  <Badge className="bg-red-600">Turismo Pista</Badge>
                  Link de carpeta en la nube
                </Label>
                <Input
                  id="turismo_drive"
                  value={driveConfig.turismo_pista_folder_id}
                  onChange={(e) => setDriveConfig(prev => ({ ...prev, turismo_pista_folder_id: e.target.value }))}
                  placeholder="Link de Google Drive, IceDrive u otro servicio..."
                  className="bg-neutral-800 border-neutral-600 text-white"
                />
                <p className="text-xs text-gray-500">
                  Ejemplo: https://drive.google.com/drive/folders/... o https://icedrive.net/s/...
                </p>
              </div>

              {/* CarShow */}
              <div className="space-y-2">
                <Label htmlFor="carshow_drive" className="text-white flex items-center gap-2">
                  <Badge className="bg-orange-600">CarShow Santafesino</Badge>
                  Link de carpeta en la nube
                </Label>
                <Input
                  id="carshow_drive"
                  value={driveConfig.carshow_folder_id}
                  onChange={(e) => setDriveConfig(prev => ({ ...prev, carshow_folder_id: e.target.value }))}
                  placeholder="Link de Google Drive, IceDrive u otro servicio..."
                  className="bg-neutral-800 border-neutral-600 text-white"
                />
                <p className="text-xs text-gray-500">
                  Deja vacío si no hay carpeta configurada
                </p>
              </div>

              {/* Turismo Nacional */}
              <div className="space-y-2">
                <Label htmlFor="tn_drive" className="text-white flex items-center gap-2">
                  <Badge className="bg-blue-600">Turismo Nacional</Badge>
                  Link de carpeta en la nube
                </Label>
                <Input
                  id="tn_drive"
                  data-testid="tn-folder-input"
                  value={driveConfig.turismo_nacional_folder_id}
                  onChange={(e) => setDriveConfig(prev => ({ ...prev, turismo_nacional_folder_id: e.target.value }))}
                  placeholder="Link de Google Drive, IceDrive u otro servicio..."
                  className="bg-neutral-800 border-neutral-600 text-white"
                />
                <p className="text-xs text-gray-500">
                  Carpeta raíz opcional. Si la dejás vacía, los pilotos verán las 14 fechas configuradas en la pestaña <span className="text-blue-400">Fechas</span>.
                </p>
              </div>

              <div className="pt-4 border-t border-neutral-700">
                <Button 
                  onClick={saveDriveConfig} 
                  disabled={savingDriveConfig}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {savingDriveConfig ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar Configuración
                </Button>
              </div>

              {/* Info Box */}
              <div className="p-4 bg-blue-950/30 border border-blue-900/50 rounded-lg">
                <p className="text-sm text-blue-300">
                  <strong>Nota:</strong> La carpeta debe ser pública (accesible sin login). 
                  Para Google Drive: compartir con &quot;Cualquier persona con el link&quot;.
                  Los cambios se aplicarán inmediatamente para todos los usuarios.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Table */}
        {activeTab === 'users' && (
          <Card className="bg-neutral-900 border-neutral-700">
            <CardHeader>
              <CardTitle className="text-white">Usuarios Registrados</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-700">
                      <th className="text-left py-3 px-4 text-gray-400">Email</th>
                      <th className="text-left py-3 px-4 text-gray-400">Nombre</th>
                      <th className="text-left py-3 px-4 text-gray-400">Plan</th>
                      <th className="text-left py-3 px-4 text-gray-400">Días Restantes</th>
                      <th className="text-left py-3 px-4 text-gray-400">Permisos</th>
                      <th className="text-left py-3 px-4 text-gray-400">Registro</th>
                      <th className="text-left py-3 px-4 text-gray-400">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id} className="border-b border-neutral-800 hover:bg-neutral-800/50">
                        <td className="py-3 px-4 text-white">{user.email}</td>
                        <td className="py-3 px-4 text-gray-300">{user.name || '-'}</td>
                        <td className="py-3 px-4">
                          {user.plan === 'pro' ? (
                            <Badge className="bg-gradient-to-r from-yellow-500 to-orange-500 text-black">
                              <Crown className="w-3 h-3 mr-1" />
                              PRO
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-gray-400">Free</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {user.days_remaining !== null ? (
                            <span className={user.days_remaining < 7 ? 'text-red-400' : 'text-green-400'}>
                              {user.days_remaining} días
                            </span>
                          ) : (
                            <span className="text-gray-500">-</span>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex flex-col gap-1">
                            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!!user.is_admin}
                                onChange={(e) => togglePermission(user.user_id, 'is_admin', e.target.checked, user.email)}
                                data-testid={`toggle-admin-${user.user_id}`}
                                className="w-3.5 h-3.5 accent-red-600"
                              />
                              <span className={user.is_admin ? 'text-red-400 font-semibold' : 'text-gray-500'}>Admin</span>
                            </label>
                            <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
                              <input
                                type="checkbox"
                                checked={!!user.pit_access}
                                onChange={(e) => togglePermission(user.user_id, 'pit_access', e.target.checked, user.email)}
                                data-testid={`toggle-pit-${user.user_id}`}
                                className="w-3.5 h-3.5 accent-orange-500"
                              />
                              <span className={user.pit_access ? 'text-orange-400 font-semibold' : 'text-gray-500'}>Ingeniería</span>
                            </label>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-400">{formatDate(user.created_at)}</td>
                        <td className="py-3 px-4">
                          <div className="flex flex-wrap gap-1">
                            {/* PRO buttons */}
                            {user.plan !== 'pro' && user.plan !== 'simracing' ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => activatePro(user.user_id, 30)}
                                  className="bg-green-600 hover:bg-green-700 text-xs"
                                  title="Activar PRO por 30 días"
                                  data-testid={`activate-pro-30d-${user.user_id}`}
                                >
                                  <Crown className="w-3 h-3 mr-1" />
                                  +30d
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => activatePro(user.user_id, 'custom')}
                                  className="bg-green-700 hover:bg-green-800 text-xs"
                                  title="Activar PRO por cantidad personalizada de días"
                                  data-testid={`activate-pro-custom-${user.user_id}`}
                                >
                                  <Crown className="w-3 h-3 mr-1" />
                                  …
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => activatePro(user.user_id, 365)}
                                  className="bg-yellow-600 hover:bg-yellow-700 text-xs"
                                  title="Activar PRO por 1 año"
                                >
                                  <Crown className="w-3 h-3" />
                                  +1a
                                </Button>
                              </>
                            ) : user.plan === 'pro' && (
                              <Button
                                size="sm"
                                onClick={() => deactivatePro(user.user_id)}
                                variant="outline"
                                className="border-yellow-600 text-yellow-500 text-xs"
                                title="Desactivar PRO"
                              >
                                <UserMinus className="w-3 h-3" />
                              </Button>
                            )}
                            
                            {/* Simracing buttons */}
                            {user.plan !== 'simracing' ? (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => activateSimracing(user.user_id, 30)}
                                  className="bg-orange-600 hover:bg-orange-700 text-xs"
                                  title="Activar Simracing por 30 días"
                                  data-testid={`activate-simracing-30d-${user.user_id}`}
                                >
                                  <Gamepad2 className="w-3 h-3 mr-1" />
                                  +30d
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => activateSimracing(user.user_id, 'custom')}
                                  className="bg-orange-700 hover:bg-orange-800 text-xs"
                                  title="Activar Simracing por cantidad personalizada de días"
                                  data-testid={`activate-simracing-custom-${user.user_id}`}
                                >
                                  <Gamepad2 className="w-3 h-3 mr-1" />
                                  …
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => activateSimracing(user.user_id, 365)}
                                  className="bg-red-600 hover:bg-red-700 text-xs"
                                  title="Activar Simracing por 1 año"
                                >
                                  <Gamepad2 className="w-3 h-3" />
                                  +1a
                                </Button>
                              </>
                            ) : (
                              <Button
                                size="sm"
                                onClick={() => deactivateSimracing(user.user_id)}
                                variant="outline"
                                className="border-orange-600 text-orange-500 text-xs"
                                title="Desactivar Simracing"
                              >
                                <UserMinus className="w-3 h-3" />
                              </Button>
                            )}

                            {/* Adjust days (+/-) — only relevant when user has an active paid plan */}
                            {(user.plan === 'pro' || user.plan === 'simracing') && (
                              <>
                                <Button
                                  size="sm"
                                  onClick={() => adjustDays(user.user_id, user.email, 'add')}
                                  variant="outline"
                                  className="border-green-600 text-green-400 hover:bg-green-950 text-xs"
                                  title="Sumar días a la suscripción actual"
                                  data-testid={`adjust-days-add-${user.user_id}`}
                                >
                                  <Plus className="w-3 h-3" />
                                  d
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => adjustDays(user.user_id, user.email, 'sub')}
                                  variant="outline"
                                  className="border-yellow-600 text-yellow-400 hover:bg-yellow-950 text-xs"
                                  title="Restar días a la suscripción actual"
                                  data-testid={`adjust-days-sub-${user.user_id}`}
                                >
                                  <Minus className="w-3 h-3" />
                                  d
                                </Button>
                              </>
                            )}

                            {/* Delete account — always last, red, asks for confirmation + email typing */}
                            {!user.is_admin && (
                              <Button
                                size="sm"
                                onClick={() => deleteUser(user.user_id, user.email)}
                                variant="outline"
                                className="border-red-700 text-red-500 hover:bg-red-950 text-xs"
                                title="Eliminar cuenta (irreversible)"
                                data-testid={`delete-user-${user.user_id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payments Table */}
        {activeTab === 'payments' && (
          <Card className="bg-neutral-900 border-neutral-700">
            <CardHeader>
              <CardTitle className="text-white">Historial de Pagos</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-700">
                      <th className="text-left py-3 px-4 text-gray-400">Email</th>
                      <th className="text-left py-3 px-4 text-gray-400">Plan</th>
                      <th className="text-left py-3 px-4 text-gray-400">Monto</th>
                      <th className="text-left py-3 px-4 text-gray-400">Estado</th>
                      <th className="text-left py-3 px-4 text-gray-400">Fecha</th>
                      <th className="text-left py-3 px-4 text-gray-400">Payment ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payments.map((payment, idx) => (
                      <tr key={idx} className="border-b border-neutral-800 hover:bg-neutral-800/50">
                        <td className="py-3 px-4 text-white">{payment.email}</td>
                        <td className="py-3 px-4 text-gray-300">
                          {payment.plan_id === 'pro_annual' ? 'Anual' : 'Mensual'}
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          ${payment.amount?.toLocaleString()} {payment.currency}
                        </td>
                        <td className="py-3 px-4">
                          {payment.payment_status === 'approved' ? (
                            <Badge className="bg-green-600">Aprobado</Badge>
                          ) : payment.payment_status === 'pending' ? (
                            <Badge className="bg-yellow-600">Pendiente</Badge>
                          ) : (
                            <Badge className="bg-red-600">{payment.payment_status}</Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-400">{formatDate(payment.created_at)}</td>
                        <td className="py-3 px-4 text-gray-500 text-xs">{payment.payment_id || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
